import React, { useEffect, useState } from "react";
import { SwipeableDrawer, useMediaQuery } from "@mui/material";
import styled from "styled-components";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";

/**
 * The platform's slide-in side panel: a right-hand drawer on desktop, a
 * drag-to-close bottom sheet on mobile.
 *
 * Lifted verbatim from `ClientSidePanel` — the prescription drawer — which
 * hardcodes its content (`NewPrescriptionWidget`), is mounted once by
 * `WorkspaceShell` in client mode only, and is opened through a
 * prescription-shaped context. None of that works for a page that also renders
 * in the affiliate shell, so the mechanics live here and the content comes in
 * as `children`. `ScoreSidePanel` is a third copy of the same shell; both are
 * candidates to migrate onto this one.
 *
 * The panel positions itself `fixed`, so render it anywhere — it does not have
 * to be mounted by a shell.
 */
const DRAG_THRESHOLD_PX = 50;
const MOBILE_BREAKPOINT = 600;
const DESKTOP_SIDEBAR_WIDTH = 600;
const BOTTOM_SHEET_HEIGHT = "70vh";

interface SidePanelProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  /** Desktop width in px. Defaults to the platform's 600. */
  width?: number;
  /**
   * Dim and block the page behind the panel. The prescription drawer dims only
   * in edit mode, so this stays a caller's choice.
   */
  showBackdrop?: boolean;
}

const DesktopPanelWrapper = styled.div<{
  $isOpen: boolean;
  $width: number;
  theme: Theme;
}>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: ${({ $width }) => $width}px;
  background-color: ${({ theme }) => theme.colors_ds.NEUTRAL_BG_MAIN};
  transform: ${({ $isOpen }) =>
    $isOpen ? "translateX(0)" : "translateX(100%)"};
  transition: transform 0.3s ease-in-out;
  z-index: 1200;
  box-shadow: ${({ $isOpen }) =>
    $isOpen ? "-2px 0 8px rgba(0, 0, 0, 0.1)" : "none"};
  /* The panel itself never scrolls: its content pins a header and a footer and
     scrolls the middle through ScrollableWrapper. Letting the drawer scroll
     instead (as the prescription drawer does, since its body manages its own
     layout) carries the footer off-screen on a short viewport. The flex column
     is so a full-height child resolves against this box. */
  overflow: hidden;
  display: flex;
  flex-direction: column;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: none;
  }
`;

const MobileBottomSheetContent = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`;

const DragHandleContainer = styled.div<{ $isDragging: boolean; theme: Theme }>`
  display: flex;
  justify-content: center;
  cursor: ${({ $isDragging }) => ($isDragging ? "grabbing" : "grab")};
  flex-shrink: 0;
  user-select: none;
  touch-action: none;
`;

const DragHandle = styled.div<{ theme: Theme }>`
  margin-top: 16px;
  width: 48px;
  height: 3px;
  border-radius: 20px;
  background-color: ${({ theme }) => theme.colors_ds.NEUTRAL_BORDER_DEFAULT};
`;

const MobileBottomSheetBody = styled.div`
  flex: 1;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 5px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #f1f1f1;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #abaaaa;
  }

  scrollbar-color: #f1f1f1 transparent;
  scrollbar-width: thin;
`;

const Backdrop = styled.div<{ $isVisible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1199;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  visibility: ${({ $isVisible }) => ($isVisible ? "visible" : "hidden")};
  transition: opacity 0.3s ease-in-out, visibility 0.3s ease-in-out;
  cursor: pointer;
`;

const SidePanel: React.FC<SidePanelProps> = ({
  open,
  onClose,
  children,
  width = DESKTOP_SIDEBAR_WIDTH,
  showBackdrop = false,
}) => {
  const theme: Theme = useTheme();
  const isDesktop = useMediaQuery(`(min-width:${MOBILE_BREAKPOINT + 1}px)`);

  // Drag to close, mobile only.
  const [dragStartY, setDragStartY] = useState<number | null>(null);
  const [dragCurrentY, setDragCurrentY] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleDragStart = (clientY: number) => {
    setDragStartY(clientY);
    setDragCurrentY(clientY);
    setIsDragging(true);
  };

  const handleDragMove = (clientY: number) => {
    if (dragStartY !== null && isDragging) {
      const deltaY = clientY - dragStartY;
      if (deltaY > 0) setDragCurrentY(clientY);
    }
  };

  const handleDragEnd = () => {
    if (dragStartY !== null && dragCurrentY !== null) {
      if (dragCurrentY - dragStartY > DRAG_THRESHOLD_PX) onClose();
    }
    setDragStartY(null);
    setDragCurrentY(null);
    setIsDragging(false);
  };

  /** Follows the finger while dragging, for visual feedback. */
  const getTranslateY = () => {
    if (dragStartY !== null && dragCurrentY !== null && isDragging) {
      const deltaY = dragCurrentY - dragStartY;
      return deltaY > 0 ? deltaY : 0;
    }
    return 0;
  };

  // A mouse drag can leave the handle, so the move/up listeners go on the
  // document rather than the element.
  useEffect(() => {
    if (!isDragging) return;

    const handleGlobalMouseMove = (e: MouseEvent) => {
      e.preventDefault();
      handleDragMove(e.clientY);
    };
    const handleGlobalMouseUp = () => handleDragEnd();

    document.addEventListener("mousemove", handleGlobalMouseMove);
    document.addEventListener("mouseup", handleGlobalMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleGlobalMouseMove);
      document.removeEventListener("mouseup", handleGlobalMouseUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging, dragStartY, dragCurrentY]);

  return (
    <>
      {isDesktop && showBackdrop && (
        <Backdrop $isVisible={open} onClick={onClose} />
      )}

      {isDesktop && (
        <DesktopPanelWrapper $isOpen={open} $width={width} theme={theme}>
          {children}
        </DesktopPanelWrapper>
      )}

      {!isDesktop && (
        <SwipeableDrawer
          anchor="bottom"
          open={open}
          onClose={onClose}
          onOpen={() => {}}
          disableSwipeToOpen={true}
          hideBackdrop={true}
          disableScrollLock={true}
          disableEnforceFocus={true}
          disableAutoFocus={true}
          disableRestoreFocus={true}
          ModalProps={{
            keepMounted: true,
            disablePortal: false,
            BackdropProps: {
              style: {
                backgroundColor: "transparent",
                pointerEvents: "none",
              },
            },
            style: { pointerEvents: "none" },
          }}
          PaperProps={{
            sx: {
              height: BOTTOM_SHEET_HEIGHT,
              borderTopLeftRadius: "26px",
              borderTopRightRadius: "26px",
              overflow: "hidden",
              pointerEvents: "auto",
              boxShadow: "0 -6px 7.6px 4px rgba(0, 0, 0, 0.10)",
              transform: `translateY(${getTranslateY()}px)`,
              transition: isDragging ? "none" : "transform 0.3s ease-out",
              backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
            },
          }}
        >
          <MobileBottomSheetContent>
            <DragHandleContainer
              $isDragging={isDragging}
              theme={theme}
              onMouseDown={(e) => {
                e.preventDefault();
                handleDragStart(e.clientY);
              }}
              onTouchStart={(e) => handleDragStart(e.touches[0].clientY)}
              onTouchMove={(e) => {
                if (isDragging) handleDragMove(e.touches[0].clientY);
              }}
              onTouchEnd={handleDragEnd}
            >
              <DragHandle theme={theme} />
            </DragHandleContainer>
            <MobileBottomSheetBody>{children}</MobileBottomSheetBody>
          </MobileBottomSheetContent>
        </SwipeableDrawer>
      )}
    </>
  );
};

export default SidePanel;
